import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function MobileRechargeForm({ show, onHide, onSuccess }) {
  // const { user } = useAuth(); // Commented out as not used
  const [balance, setBalance] = useState(null);
  const [mobile, setMobile] = useState('');
  const [operator, setOperator] = useState('');
  const [amount, setAmount] = useState('');
  const [type] = useState('mobile'); // setType removed as not used
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [operators, setOperators] = useState([]);
  const [plans, setPlans] = useState([]);

  // Fetch wallet balance on mount
  useEffect(() => {
    fetchBalance();
    fetchOperators();
  }, []);

  const fetchBalance = async () => {
    try {
      setBalanceLoading(true);
      const response = await api.get('/api/wallet/balance');
      if (response.data.success) {
        setBalance(response.data.balance);
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      toast.error('Failed to load wallet balance');
      setBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const response = await api.get('/api/recharge/operators?type=mobile');
      if (response.data.success) {
        setOperators(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch operators:', error);
    }
  };

  const fetchPlans = async (operator) => {
    if (!operator) return;
    
    try {
      const response = await api.get(`/api/recharge/plans?operator=${operator}&type=mobile`);
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  // Removed unused validateAmount function

  // Validate amount when it changes
  useEffect(() => {
    if (balance === null) {
      setError('');
      return;
    }
    
    if (!amount) {
      setError('');
      return;
    }
    
    // Immediate validation for insufficient balance
    if (Number(amount) > Number(balance)) {
      setError('Aapka balance kaafi nahi hai');
    } else {
      setError('');
    }
  }, [amount, balance]);

  // Fetch plans when operator changes
  useEffect(() => {
    if (operator) {
      fetchPlans(operator);
    }
  }, [operator]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (error || !amount || !mobile || !operator) {
      return;
    }

    setLoading(true);
    try {
      // Generate idempotency key for this request
      const idempotencyKey = uuidv4();
      
      const response = await api.post('/api/recharge/process', {
        amount: Number(amount),
        mobile: mobile,
        operator: operator,
        type: type,
        idempotency_key: idempotencyKey
      });

      // Only trust server responses with success=true AND transaction_id present
      if (response.data.success && response.data.transaction_id) {
        toast.success(response.data.message || 'Recharge successful');
        
        // Update balance
        setBalance(response.data.new_balance);
        
        // Call onSuccess callback for modal
        if (onSuccess) {
          onSuccess({
            transaction_id: response.data.transaction_id,
            amount: response.data.amount,
            mobile: response.data.mobile,
            operator: response.data.operator,
            new_balance: response.data.new_balance
          });
        }
        
        // Reset form
        setAmount('');
        setMobile('');
        setOperator('');
        setError('');
        
        // Close modal after success
        setTimeout(() => {
          if (onHide) onHide();
        }, 2000);
      } else {
        setError(response.data.message || 'Recharge failed');
        toast.error(response.data.message || 'Recharge failed');
      }
    } catch (error) {
      console.error('Recharge failed:', error);
      
      if (error.response) {
        const data = error.response.data || {};
        if (data.code === 'INSUFFICIENT_FUNDS') {
          setError('Aapka balance kaafi nahi hai (server)');
          toast.error(data.message || 'Insufficient balance');
        } else if (data.code === 'RETRY_LATER') {
          setError('Request in progress. Please wait...');
          toast.error('Request in progress. Please wait...');
        } else {
          setError(data.message || 'Recharge failed');
          toast.error(data.message || 'Recharge failed');
        }
      } else {
        setError('Network error');
        toast.error('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = !error && amount && mobile && operator && !loading;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="bi bi-phone me-2"></i>
          Mobile Recharge
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Balance Display */}
        <div className="mb-4">
          <label className="form-label fw-bold">Available Balance</label>
          <div className="d-flex align-items-center">
            {balanceLoading ? (
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <span className="fs-4 text-success fw-bold">
                ₹{balance !== null ? balance.toFixed(2) : '0.00'}
              </span>
            )}
            <button 
              className="btn btn-sm btn-outline-secondary ms-2"
              onClick={fetchBalance}
              disabled={balanceLoading}
            >
              <i className="bi bi-arrow-clockwise"></i> Refresh
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Mobile Number Input */}
          <div className="mb-3">
            <label htmlFor="mobile" className="form-label">
              Mobile Number <span className="text-danger">*</span>
            </label>
            <input
              type="tel"
              className="form-control"
              id="mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Enter 10-digit mobile number"
              maxLength="10"
              disabled={loading}
              required
            />
          </div>

          {/* Operator Selection */}
          <div className="mb-3">
            <label htmlFor="operator" className="form-label">
              Operator <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              id="operator"
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              disabled={loading}
              required
            >
              <option value="">Select Operator</option>
              {operators.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.name} ({op.type})
                </option>
              ))}
            </select>
          </div>

          {/* Amount Input */}
          <div className="mb-3">
            <label htmlFor="amount" className="form-label">
              Amount <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input
                type="number"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                step="1"
                disabled={loading}
              />
            </div>
            {error && (
              <div className="invalid-feedback d-block">
                {error}
              </div>
            )}
            {balance !== null && amount && !error && (
              <div className="form-text text-success">
                Remaining balance: ₹{(balance - Number(amount)).toFixed(2)}
              </div>
            )}
          </div>

          {/* Quick Amount Buttons */}
          <div className="mb-3">
            <label className="form-label">Quick Amounts</label>
            <div className="d-flex flex-wrap gap-2">
              {[10, 20, 50, 100, 199, 299, 399, 499, 599, 999].map((quickAmount) => (
                <button
                  key={quickAmount}
                  type="button"
                  className={`btn btn-outline-primary btn-sm ${
                    amount === quickAmount.toString() ? 'active' : ''
                  }`}
                  onClick={() => setAmount(quickAmount.toString())}
                  disabled={loading}
                >
                  ₹{quickAmount}
                </button>
              ))}
            </div>
          </div>

          {/* Plans Display */}
          {plans.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Available Plans</label>
              <div className="row">
                {plans.slice(0, 3).map((plan, index) => (
                  <div key={index} className="col-md-4 mb-2">
                    <div className="card">
                      <div className="card-body p-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">₹{plan.amount}</span>
                          {plan.description && (
                            <span className="badge bg-success">{plan.description}</span>
                          )}
                        </div>
                        <div className="small text-muted">
                          <div>Validity: {plan.validity}</div>
                          <div>Data: {plan.data}</div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary w-100 mt-2"
                          onClick={() => setAmount(plan.amount.toString())}
                          disabled={loading}
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="d-grid">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-phone me-2"></i>
                  Recharge Now
                </>
              )}
            </button>
          </div>

          {/* Balance Warning */}
          {balance !== null && balance === 0 && (
            <div className="alert alert-danger mt-3" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Insufficient Balance:</strong> Your wallet balance is ₹0. Please add funds to recharge.
            </div>
          )}
          {balance !== null && balance > 0 && balance < 100 && (
            <div className="alert alert-warning mt-3" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Low balance warning: Your wallet balance is below ₹100
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
